/**
 * The Vitesse half of a payment method's `payment_provider_specific` configuration: the shape
 * normalization applied on the way in and on the way out, the initial-values default for the API
 * selector, and the Yup rules the generic validation schema spreads in.
 */

import * as Yup from 'yup';

import VITESSE_APIS from '~/server_shared/generated-types/VITESSE_APIS';
import VITESSE_VERIFICATION_METHOD_SOURCES from '~/server_shared/generated-types/VITESSE_VERIFICATION_METHOD_SOURCES';
import type { VitesseProgrammeCheckArguments } from '~/server_shared/openapi/data-contracts';

import type { PaymentMethodConfigurationValues } from '../types';

type VitesseApi = keyof typeof VITESSE_APIS;

/**
 * The stored `payment_provider_specific` document as the form holds it.
 *
 * Typed off the generated `VitesseProgrammeCheckArguments` contract — the seven keys the backend
 * shares between the Verify endpoint and its config model — plus two form-side widenings:
 * `vitesse_api`, which the config model declares alone (the Verify button does not care which rail a
 * method runs on), and `vitesse_programme_id` relaxed to optional because a half-filled form legally
 * has none yet. Every reader server-side uses `.get()`, so an unknown key is tolerated here too.
 */
export type VitesseProviderSpecificConfig = Omit<
  VitesseProgrammeCheckArguments,
  'vitesse_programme_id' | 'vitesse_verification_method_source'
> & {
  vitesse_programme_id?: string;
  vitesse_api?: VitesseApi;
  /**
   * `''` is form state only: MUI needs a string for the select to stay controlled, and
   * `withVitesseVerificationOutgoing` turns it back into `null` before it is persisted.
   */
  vitesse_verification_method_source?: VitesseProgrammeCheckArguments['vitesse_verification_method_source'] | '';
};

/**
 * A row holding only a verification method id and a configured field key predates the SMS/passphrase
 * flags, and meant exactly one thing: a passphrase read from that field. Infer the flags so the boxes
 * reflect what the row does — saving replaces method_configuration wholesale rather than merging, so
 * unchecked boxes would silently turn a live payee's passphrase off. Stored flags always win. Mirrors
 * vitesse_utils.normalize_verification_config, and stays for good: a config-repo snapshot can
 * re-import the old shape at any time.
 */
export const withVitesseVerificationDefaults = (
  paymentProviderSpecific: VitesseProviderSpecificConfig | null | undefined
): VitesseProviderSpecificConfig => {
  // Nullish-guarded like the sibling read in VitessePaymentProviderConfig: lodash `merge` keeps an
  // explicit null from the saved row rather than falling back to the default `{}`.
  const savedConfig = paymentProviderSpecific || {};
  const hasLegacyPassphrase =
    !('vitesse_verification_sms_enabled' in savedConfig) &&
    !('vitesse_verification_passphrase_enabled' in savedConfig) &&
    !!savedConfig.vitesse_verification_method_id &&
    !!savedConfig.vitesse_verification_method_field_key;

  return {
    vitesse_verification_sms_enabled: false,
    vitesse_verification_passphrase_enabled: hasLegacyPassphrase,
    vitesse_verification_method_source: hasLegacyPassphrase ? 'incident_configured_field' : '',
    ...savedConfig,
  };
};

/**
 * The outgoing counterpart of `withVitesseVerificationDefaults`. The source is kept as `''` in form
 * state because that is what the MUI select needs (a null value makes it an uncontrolled input), but
 * `''` must never be *persisted*: it is neither a valid source nor "unset" to the backend, which reads
 * the key as an optional enum. Normalizing here rather than only at the seeded default also catches a
 * `''` that is already stored — a row saved by an older bundle, or re-imported from a config-repository
 * snapshot taken while one was live. (The select itself cannot produce one: `TextFieldFormik` is only
 * clearable when asked, and the passphrase source does not ask.)
 */
const withVitesseVerificationOutgoing = (
  paymentProviderSpecific: VitesseProviderSpecificConfig | null | undefined
): VitesseProviderSpecificConfig => {
  const savedConfig = paymentProviderSpecific || {};
  if (savedConfig.vitesse_verification_method_source) {
    return savedConfig;
  }
  return { ...savedConfig, vitesse_verification_method_source: null };
};

/** The form's values with the Vitesse document named, for the helpers in this module. */
export type VitesseConfigurationValues = PaymentMethodConfigurationValues<VitesseProviderSpecificConfig>;

/**
 * `withVitesseVerificationOutgoing` applied to a whole set of form values on submit.
 *
 * Applied at the form's own submit rather than in `processOutgoingConfigValues`
 * (`PaymentsConfigurationTab`), which is otherwise the real persistence seam: it is handed only
 * `values`, and deciding whether to normalize needs the payment method — `PaymentConfigTab` keeps that
 * in `activePaymentMethodConfig` and passes it to `handleFormSubmit` separately, so moving the rule
 * there would mean widening that prop's contract for every payments configuration screen.
 */
export const withVitesseVerificationOutgoingValues = (
  values: VitesseConfigurationValues,
  { isCustomMethod }: { isCustomMethod: boolean }
): VitesseConfigurationValues => {
  if (values.method_configuration?.payment_provider !== 'vitesse' || !isCustomMethod) {
    return values;
  }
  return {
    ...values,
    method_configuration: {
      ...values.method_configuration,
      payment_provider_specific: withVitesseVerificationOutgoing(values.method_configuration.payment_provider_specific),
    },
  };
};

/**
 * The initial-values merge layer for a Vitesse configuration, shaped for `merge()` rather than applied
 * directly so a persisted value wins over the default.
 *
 * The Vitesse API select is required and must mount already holding a value: MUI freezes whether an
 * input is controlled at mount (see the InputBase note in core/Molecules/Fields/TextField), so a value
 * supplied afterwards never reaches the dropdown. Default it to the current API — mirroring
 * DEFAULT_VITESSE_API in the vitesse module and _DEFAULT_VITESSE_API in payments_config. Scoped to a
 * configuration that is actually on Vitesse, so a UK method on another provider does not carry a stray
 * key into its saved configuration.
 */
export const getVitesseDefaultProviderSpecificConfig = ({
  paymentMethodName,
  paymentProvider,
}: {
  paymentMethodName: string;
  paymentProvider?: string | null;
}): { method_configuration?: { payment_provider_specific: VitesseProviderSpecificConfig } } =>
  paymentMethodName === 'uk_bank_transfer' && paymentProvider === 'vitesse'
    ? { method_configuration: { payment_provider_specific: { vitesse_api: 'payment_requests' } } }
    : {};

/**
 * The `vitesse_account_id` rule, for both of the shapes below.
 *
 * Kept as one expression because the two branches disagree about *when* the account id is required and
 * never about the one condition that removes the question entirely: with the fund-account engine
 * resolving the send account per payment, Vitesse's own configured account is not the one being used.
 * The caller decides when that holds — `isFundAccountResolvedByRules` reads the engine's feature flag as
 * well as the block, matching `account_id_resolved_by_rules` in `payments_config_manager`, which relaxes
 * the server's own rule on both. A branch that still asked on that condition would block a save the
 * backend accepts; one that stopped asking on the block alone would drop the rule while the server kept
 * it.
 *
 * It is unconditional on the payee-choice side otherwise: every payee-choice payment goes out through
 * Payment Requests, whose payload builder (`_resolve_send_account_id`, `vitesse_api_manager`) raises
 * rather than sending without a SendAccountId.
 */
const getVitesseAccountIdValidation = ({
  isCustomMethod,
  paymentMethodName,
  isFundAccountSourceRules,
}: {
  isCustomMethod: boolean;
  paymentMethodName: string;
  isFundAccountSourceRules: boolean;
}): Yup.AnySchema => {
  if (isFundAccountSourceRules) {
    return Yup.string();
  }
  if (isCustomMethod) {
    return Yup.string().required('Required');
  }
  // Mirrors the server rule in the vitesse module. The Payment-Requests flow requires a send account
  // id; the legacy Transactions API tolerates a missing one. au_bank_transfer / nz_bank_transfer /
  // check / echeck / us_ach are Payment-Requests-only so they always need one; uk_bank_transfer needs
  // one only when it is on Payment Requests. Vitesse account ids can be alphanumeric (typed str | int
  // server-side), so validate as a string.
  if (['au_bank_transfer', 'nz_bank_transfer', 'check', 'echeck', 'us_ach'].includes(paymentMethodName)) {
    return Yup.string().required('Required');
  }
  if (paymentMethodName === 'uk_bank_transfer') {
    return Yup.string().when('vitesse_api', (vitesse_api, schema) =>
      (vitesse_api || 'payment_requests') === 'payment_requests' ? schema.required('Required') : schema
    );
  }
  return Yup.string();
};

/**
 * The Vitesse rules for `method_configuration.payment_provider_specific`, as a Yup shape object for
 * the generic schema to spread in under its `payment_provider === 'vitesse'` condition.
 *
 * Two branches, because a Vitesse method is either a payee-choice (custom) method — where the programme
 * and the payee-verification setup are what matters — or a fixed-rail one, where which rail it runs on
 * is. `vitesse_account_id` is the one key both branches ask for, and both take it from
 * `getVitesseAccountIdValidation` above.
 *
 * The payee-choice branch is stricter than the server's `validate_provider_specific_config`, which asks
 * a custom method for no account id at all — pre-existing on both sides, and deliberately left alone
 * here: this form is the only writer that can ask an operator for the value while they are looking at
 * it, and relaxing it to match would let a configuration save that the send path then cannot use. Only
 * the rules-sourced case is relaxed, and that one the server relaxes too.
 */
export const getVitesseProviderSpecificValidationShape = ({
  isCustomMethod,
  paymentMethodName,
  isFundAccountSourceRules,
}: {
  isCustomMethod: boolean;
  paymentMethodName: string;
  isFundAccountSourceRules: boolean;
}): Record<string, Yup.AnySchema> => {
  const vitesseAccountId = getVitesseAccountIdValidation({
    isCustomMethod,
    paymentMethodName,
    isFundAccountSourceRules,
  });
  return isCustomMethod
    ? {
        vitesse_account_id: vitesseAccountId,
        vitesse_programme_id: Yup.string().required('Required'),
        vitesse_verification_sms_enabled: Yup.boolean(),
        vitesse_verification_passphrase_enabled: Yup.boolean(),
        vitesse_verification_method_id: Yup.string().when('vitesse_verification_passphrase_enabled', {
          is: true,
          then: (schema) => schema.required('Required'),
          otherwise: (schema) => schema.nullable(),
        }),
        vitesse_verification_method_source: Yup.string().when('vitesse_verification_passphrase_enabled', {
          is: true,
          then: (schema) =>
            schema.oneOf(Object.keys(VITESSE_VERIFICATION_METHOD_SOURCES), 'Required').required('Required'),
          otherwise: (schema) => schema.nullable(),
        }),
        vitesse_verification_method_field_key: Yup.string().when(
          ['vitesse_verification_passphrase_enabled', 'vitesse_verification_method_source'],
          {
            is: (isPassphraseEnabled: boolean, source: string) =>
              isPassphraseEnabled && source === 'incident_configured_field',
            then: (schema) => schema.required('Required'),
            otherwise: (schema) => schema.nullable(),
          }
        ),
      }
    : {
        vitesse_account_id: vitesseAccountId,
        // uk_bank_transfer is the only method that renders or writes vitesse_api, so it is the only one
        // that declares it.
        ...(paymentMethodName === 'uk_bank_transfer'
          ? { vitesse_api: Yup.string().oneOf(Object.keys(VITESSE_APIS)).required('Required') }
          : {}),
      };
};
